//! Imperative bridge between the React `CellEditorView` inline editor and the
//! grid's inline-editor handler. Replaces the previous Monaco-based surface.

import type { CellEditorViewHandle } from '@/app/cellEditor/CellEditorView';
import { inlineEditorEvents } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorEvents';
import { inlineEditorHandler } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorHandler';
import { inlineEditorSpans } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorSpans';
import { emojiMap } from '@/app/gridGL/pixiApp/emojis/emojiMap';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { CURSOR_THICKNESS } from '@/app/gridGL/UI/Cursor';
import type { CellAlign, CellVerticalAlign, CellWrap } from '@/app/quadratic-core-types';
import { xyToA1 } from '@/app/quadratic-core/quadratic_core';
import { LINE_HEIGHT } from '@/app/web-workers/renderWebWorker/worker/cellsLabel/CellLabel';
import { DEFAULT_FONT_SIZE } from '@/shared/constants/gridConstants';

export const EMOJI_FONTS = "NotoColorEmoji, 'Apple Color Emoji', 'Segoe UI Emoji'";

export const PADDING_FOR_INLINE_EDITOR = 5;

const BASE_PADDING_FOR_WIDTH = 15;
const LAYOUT_WIDTH_BUFFER = 4;

export type InlineEditorViewportBounds = {
  maxWidth: number;
  maxHeight: number;
};

export interface InlineEditorContentChange {
  rangeOffset: number;
  rangeLength: number;
  text: string;
}

/**
 * Diff two strings into a single minimal content change (the common prefix and
 * suffix are trimmed). Returns undefined when the values are identical. Shared by
 * the inline editor and the formula bar so span tracking can shift consistently.
 */
export function computeContentChange(prevValue: string, newValue: string): InlineEditorContentChange | undefined {
  if (prevValue === newValue) return undefined;
  let rangeOffset = 0;
  while (
    rangeOffset < prevValue.length &&
    rangeOffset < newValue.length &&
    prevValue[rangeOffset] === newValue[rangeOffset]
  ) {
    rangeOffset++;
  }
  let prevEnd = prevValue.length;
  let newEnd = newValue.length;
  while (prevEnd > rangeOffset && newEnd > rangeOffset && prevValue[prevEnd - 1] === newValue[newEnd - 1]) {
    prevEnd--;
    newEnd--;
  }
  return { rangeOffset, rangeLength: prevEnd - rangeOffset, text: newValue.slice(rangeOffset, newEnd) };
}

type ValueSetter = (value: string, caret?: number | 'all') => void;

class InlineEditorInput {
  private handle?: CellEditorViewHandle;
  private container?: HTMLDivElement;
  private setValueExternal?: ValueSetter;

  autocompleteList?: string[];
  autocompleteShowingList = false;
  emojiShowingList = false;

  private measureCanvas?: HTMLCanvasElement;
  private processingEmojiConversion = false;
  private fontSize = DEFAULT_FONT_SIZE;
  private fontFamily = `OpenSans, ${EMOJI_FONTS}`;

  private styleRoot(): HTMLElement | undefined {
    return (this.container?.closest('#cell-edit') as HTMLElement | null) ?? this.container;
  }

  bind(handle: CellEditorViewHandle, container: HTMLDivElement, setValue: ValueSetter) {
    this.handle = handle;
    this.container = container;
    this.setValueExternal = setValue;
  }

  unbind() {
    this.handle = undefined;
    this.container = undefined;
    this.setValueExternal = undefined;
  }

  get = (): string => {
    if (!this.handle) return '';
    return this.handle.getValue();
  };

  set(s: string, select?: boolean | number) {
    if (!this.setValueExternal) return;
    // Write the DOM value synchronously so get() reflects it before React
    // re-renders. Otherwise a caller that sets a value and immediately reads it
    // (e.g. selecting an autocomplete value then closing the editor) would read
    // the stale, pre-set textarea value and commit that instead.
    const el = this.handle?.getElement();
    if (el) el.value = s;
    if (select === true) {
      this.setValueExternal(s, 'all');
    } else if (typeof select === 'number') {
      this.setValueExternal(s, select);
    } else {
      this.setValueExternal(s, s.length);
    }
  }

  focus = () => {
    inlineEditorHandler.keepCursorVisible();
    this.handle?.focus();
  };

  blur = () => {
    this.handle?.blur();
  };

  getCaret = (): number => this.handle?.getCaret() ?? 0;

  getCursorColumn = (): number => {
    const caret = this.getCaret();
    const text = this.get();
    const lineStart = text.lastIndexOf('\n', caret - 1) + 1;
    return caret - lineStart + 1;
  };

  hasSelection(): boolean {
    const el = this.handle?.getElement();
    if (!el) return false;
    return el.selectionStart !== el.selectionEnd;
  }

  selectAll() {
    const el = this.handle?.getElement();
    if (!el) return;
    el.setSelectionRange(0, el.value.length);
  }

  getSelection(): { text: string; start: number; end: number } | undefined {
    const el = this.handle?.getElement();
    if (!el || el.selectionStart === el.selectionEnd) return undefined;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    return { text: el.value.slice(start, end), start, end };
  }

  removeSelection() {
    const sel = this.getSelection();
    if (!sel) return;
    this.replaceRange('', sel.start, sel.end, sel.start);
  }

  deleteText(position: number, length: number) {
    this.replaceRange('', position, position + length, position);
  }

  replaceRange(text: string, start: number, end: number, caretOffset?: number) {
    this.handle?.replaceRange(start, end, text, caretOffset);
  }

  insertTextAtCursor(text: string): number {
    const caret = this.getCaret();
    this.handle?.insertAtCaret(text);
    return caret + 1;
  }

  insertTextAtOffset(offset: number, text: string) {
    this.replaceRange(text, offset, offset, offset + text.length);
  }

  getCharBeforeCaret = (): string => this.handle?.getCharBeforeCaret() ?? '';

  getEditorSizing(): { bounds: DOMRect; position: { top: number; left: number; height: number } } {
    const container = this.container;
    const el = this.handle?.getElement();
    if (!container || !el) {
      return { bounds: new DOMRect(), position: { top: 0, left: 0, height: LINE_HEIGHT } };
    }
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || LINE_HEIGHT;
    const textBefore = el.value.slice(0, el.selectionStart ?? 0);
    const lineIndex = textBefore.split('\n').length - 1;
    return {
      bounds: container.getBoundingClientRect(),
      position: { top: lineIndex * lineHeight, left: 0, height: lineHeight },
    };
  }

  setShowingList(showing: boolean) {
    this.autocompleteShowingList = showing;
  }

  setShowingEmojiList(showing: boolean) {
    this.emojiShowingList = showing;
  }

  get autocompleteSuggestionShowing(): boolean {
    if (!this.autocompleteList) return false;
    const lowerCase = this.get().toLowerCase();
    // Empty input is a prefix of every value, so never autocomplete it; that
    // would suggest an arbitrary neighbor value for a blank cell.
    if (lowerCase.trim() === '') return false;
    const filteredList = this.autocompleteList.filter(
      (t) => t.toLowerCase().startsWith(lowerCase) && t.length > lowerCase.length
    );
    return filteredList.length === 1;
  }

  sendKeyboardEvent(e: KeyboardEvent) {
    const el = this.handle?.getElement();
    if (!el) return;
    el.dispatchEvent(new KeyboardEvent(e.type, e));
  }

  setReadOnly(readOnly: boolean) {
    const el = this.handle?.getElement();
    if (el) el.readOnly = readOnly;
  }

  setBackgroundColor(color: string) {
    const root = this.styleRoot();
    if (root) {
      root.style.backgroundColor = color;
    }
  }

  setTextColor(color: string | undefined) {
    const root = this.styleRoot();
    if (!root) return;
    if (color) {
      root.style.setProperty('--cell-text-color', color);
      root.dataset.hasTextColor = 'true';
    } else {
      root.style.removeProperty('--cell-text-color');
      delete root.dataset.hasTextColor;
    }
  }

  setCursorColor(color: string) {
    const root = this.styleRoot();
    if (root) {
      root.style.setProperty('--cell-editor-cursor-color', color);
    }
  }

  setFontFamily(fontFamily: string) {
    this.fontFamily = fontFamily;
    const root = this.styleRoot();
    if (root) {
      root.style.fontFamily = fontFamily;
    }
  }

  setFontSize(fontSize: number) {
    this.fontSize = fontSize;
    const root = this.styleRoot();
    if (root) {
      const lineHeight = (fontSize / DEFAULT_FONT_SIZE) * LINE_HEIGHT;
      root.style.fontSize = `${fontSize}px`;
      root.style.lineHeight = `${lineHeight}px`;
    }
  }

  setUnderline(underline: boolean) {
    const root = this.styleRoot();
    if (!root) return;
    if (underline && !inlineEditorHandler.formula) {
      root.dataset.underline = 'true';
    } else {
      delete root.dataset.underline;
    }
  }

  setStrikeThrough(strikeThrough: boolean) {
    const root = this.styleRoot();
    if (!root) return;
    if (strikeThrough && !inlineEditorHandler.formula) {
      root.dataset.strikeThrough = 'true';
    } else {
      delete root.dataset.strikeThrough;
    }
  }

  setItalic(italic: boolean) {
    const root = this.styleRoot();
    if (!root) return;
    if (italic && !inlineEditorHandler.formula) {
      root.dataset.italic = 'true';
    } else {
      delete root.dataset.italic;
    }
  }

  setBold(bold: boolean) {
    const root = this.styleRoot();
    if (!root) return;
    if (bold && !inlineEditorHandler.formula) {
      root.dataset.bold = 'true';
    } else {
      delete root.dataset.bold;
    }
  }

  setLanguage(_language: 'Formula' | 'inline-editor') {
    // No-op: highlighting is handled by CellEditorView / span tokens.
  }

  notifyValueChanged(prevValue: string, newValue: string) {
    this.convertEmojis();
    if (!this.processingEmojiConversion) {
      inlineEditorEvents.emit('valueChanged', newValue);
      const change = computeContentChange(prevValue, newValue);
      if (change) {
        inlineEditorEvents.emit('contentChanged', [change]);

        if (navigator.webdriver) {
          const cellEdit = document.querySelector('#cell-edit');
          if (cellEdit) {
            const loc = inlineEditorHandler.location;
            if (loc) {
              cellEdit.setAttribute('data-test-cell', xyToA1(loc.x, loc.y));
              cellEdit.setAttribute('data-test-value', newValue);
            }
          }
        }
      }
      inlineEditorSpans.refreshHighlightTokens();
    }
  }

  notifySelectionChanged() {
    inlineEditorHandler.updateMonacoCursorPosition();
    inlineEditorHandler.updateSelectionFormatting();
  }

  updateTextLayout = (
    width: number,
    height: number,
    textAlign: CellAlign,
    verticalAlign: CellVerticalAlign,
    textWrap: CellWrap,
    underline: boolean,
    strikeThrough: boolean,
    formula: boolean,
    viewportBounds?: InlineEditorViewportBounds,
    readOnlyPreview = false
  ): { width: number; height: number } => {
    const layoutEl = this.styleRoot();
    const textarea = this.handle?.getElement();
    if (!layoutEl || !textarea) {
      return { width, height };
    }

    const minHeight = height;
    const fontSize = this.fontSize;
    const fontFamily = this.fontFamily;
    const text = this.get();

    const styleRoot = this.styleRoot();
    if (styleRoot) {
      styleRoot.dataset.textAlign = textAlign;
    }
    this.setUnderline(underline);
    this.setStrikeThrough(strikeThrough);

    const fontScale = fontSize / DEFAULT_FONT_SIZE;
    const scaledPadding = !readOnlyPreview && textAlign === 'left' ? BASE_PADDING_FOR_WIDTH * fontScale : 0;
    const canvas = (this.measureCanvas ??= document.createElement('canvas'));
    const context = canvas.getContext('2d');
    let measuredWidth = 0;

    if (context) {
      measuredWidth = this.measureTextWithSpans(context, text, fontSize, fontFamily) + scaledPadding;
    } else {
      measuredWidth = textarea.scrollWidth + scaledPadding;
    }

    const horizontalOverflowsCell = measuredWidth > width;
    let effectiveWrap = textWrap;

    const viewportRectangle = pixiApp.getViewportRectangle();
    const maxWidthDueToViewport = viewportBounds?.maxWidth ?? viewportRectangle.width - 2 * PADDING_FOR_INLINE_EDITOR;

    if (readOnlyPreview) {
      if (measuredWidth + LAYOUT_WIDTH_BUFFER > maxWidthDueToViewport) {
        width = maxWidthDueToViewport;
        effectiveWrap = 'wrap';
      } else {
        // Size to the larger of the measured text and the cell width so the
        // preview never shrinks narrower than the cell it sits on.
        width = Math.min(Math.max(measuredWidth + LAYOUT_WIDTH_BUFFER, width, CURSOR_THICKNESS), maxWidthDueToViewport);
        effectiveWrap = 'clip';
      }
    } else {
      const shouldExpandWidth =
        formula || effectiveWrap === 'overflow' || (effectiveWrap === 'clip' && horizontalOverflowsCell);
      if (shouldExpandWidth) {
        width = Math.max(width, measuredWidth + LAYOUT_WIDTH_BUFFER);
      }
    }

    if (width > maxWidthDueToViewport) {
      if (formula) {
        width = maxWidthDueToViewport;
      } else {
        effectiveWrap = 'wrap';
        width = maxWidthDueToViewport;
      }
    }

    const needsHorizontalScroll = !readOnlyPreview && formula && effectiveWrap === 'clip' && measuredWidth > width;
    if (needsHorizontalScroll) {
      if (styleRoot) styleRoot.dataset.clipOverflow = 'true';
      textarea.style.overflowX = 'auto';
    } else {
      if (styleRoot) delete styleRoot.dataset.clipOverflow;
      textarea.style.overflowX = 'hidden';
      textarea.scrollLeft = 0;
    }

    const lineHeight = (fontSize / DEFAULT_FONT_SIZE) * LINE_HEIGHT;
    const wraps = effectiveWrap === 'wrap';
    const wrapStyle = wraps ? 'soft' : 'off';
    textarea.wrap = wrapStyle;
    textarea.style.whiteSpace = wraps ? 'pre-wrap' : 'pre';

    // The textarea is transparent; the visible text is the highlight overlay.
    // Keep its wrapping in sync with the textarea so what the user sees matches
    // the caret's actual line layout (otherwise long text renders on a single
    // overlay line while the textarea wraps behind it, letting the caret move
    // into "empty" vertical space).
    const overlay = this.handle?.getHighlightElement();
    if (overlay) {
      overlay.style.whiteSpace = wraps ? 'pre-wrap' : 'pre';
      overlay.style.wordBreak = wraps ? 'break-word' : 'normal';
      overlay.style.overflowWrap = wraps ? 'break-word' : 'normal';
    }

    layoutEl.style.width = `${width}px`;
    layoutEl.style.minWidth = `${width}px`;

    const contentHeight = this.estimateContentHeight(text, width, fontSize, fontFamily, lineHeight, context);
    height = Math.max(contentHeight, minHeight);

    let heightCapped = false;
    const maxHeightDueToViewport = viewportBounds?.maxHeight;
    if (maxHeightDueToViewport !== undefined && height > maxHeightDueToViewport) {
      height = maxHeightDueToViewport;
      heightCapped = true;
    }

    let paddingTop = 0;
    if (verticalAlign === 'middle') {
      paddingTop = Math.max((height - contentHeight) / 2, 0);
    } else if (verticalAlign === 'bottom') {
      paddingTop = Math.max(height - contentHeight, 0);
    }

    layoutEl.style.height = `${height}px`;
    layoutEl.style.paddingTop = `${paddingTop}px`;
    textarea.style.overflowY = heightCapped ? 'auto' : 'hidden';

    return { width, height };
  };

  private measureTextWithSpans(
    context: CanvasRenderingContext2D,
    text: string,
    fontSize: number,
    defaultFontFamily: string
  ): number {
    const lines = text.split('\n');
    let maxWidth = 0;
    let lineStartOffset = 0;
    for (const line of lines) {
      maxWidth = Math.max(
        maxWidth,
        this.measureLineWithSpans(context, line, lineStartOffset, fontSize, defaultFontFamily)
      );
      lineStartOffset += line.length + 1;
    }
    return maxWidth;
  }

  private measureLineWithSpans(
    context: CanvasRenderingContext2D,
    lineText: string,
    lineStartOffset: number,
    fontSize: number,
    defaultFontFamily: string
  ): number {
    const spans = inlineEditorSpans.getSpans();
    if (!inlineEditorSpans.isActive() || spans.length === 0) {
      context.font = `${fontSize}px ${defaultFontFamily}`;
      return context.measureText(lineText).width;
    }

    const styleRoot = this.styleRoot();
    const cellBold = styleRoot?.dataset.bold === 'true';
    const cellItalic = styleRoot?.dataset.italic === 'true';
    const defaultBold = cellBold || defaultFontFamily.includes('Bold');
    const defaultItalic = cellItalic || defaultFontFamily.includes('Italic');
    const cellFont = this.getFontForSpan(defaultBold, defaultItalic, fontSize);

    const lineEndOffset = lineStartOffset + lineText.length;
    let totalWidth = 0;
    let lastEnd = 0;
    const sortedSpans = [...spans]
      .filter((span) => span.start < lineEndOffset && span.end > lineStartOffset)
      .sort((a, b) => a.start - b.start);

    for (const span of sortedSpans) {
      const spanStartInLine = Math.max(0, span.start - lineStartOffset);
      const spanEndInLine = Math.min(lineText.length, span.end - lineStartOffset);
      if (spanStartInLine > lastEnd) {
        context.font = cellFont;
        totalWidth += context.measureText(lineText.slice(lastEnd, spanStartInLine)).width;
      }
      const spanText = lineText.slice(spanStartInLine, spanEndInLine);
      if (spanText) {
        const spanBold = span.bold !== undefined ? span.bold : defaultBold;
        const spanItalic = span.italic !== undefined ? span.italic : defaultItalic;
        context.font = this.getFontForSpan(spanBold, spanItalic, fontSize);
        totalWidth += context.measureText(spanText).width;
      }
      lastEnd = Math.max(lastEnd, spanEndInLine);
    }
    if (lastEnd < lineText.length) {
      context.font = cellFont;
      totalWidth += context.measureText(lineText.slice(lastEnd)).width;
    }
    return totalWidth;
  }

  private getFontForSpan = (bold: boolean, italic: boolean, fontSize: number): string => {
    const style = italic ? 'italic ' : '';
    const weight = bold ? 'bold ' : '';
    return `${style}${weight}${fontSize}px OpenSans, ${EMOJI_FONTS}`;
  };

  // Returns the natural (unclamped) height of the text content for the given
  // layout width. The caller clamps to the cell's min height and applies
  // vertical alignment via container padding, so this must reflect only the
  // wrapped text — not the cell height — otherwise vertical centering can't be
  // computed and content is mis-padded.
  private estimateContentHeight(
    text: string,
    layoutWidth: number,
    fontSize: number,
    fontFamily: string,
    lineHeight: number,
    context: CanvasRenderingContext2D | null
  ): number {
    if (layoutWidth <= 0) return lineHeight;
    let totalLines = 0;
    for (const paragraph of text.split('\n')) {
      if (paragraph === '') {
        totalLines += 1;
        continue;
      }
      let paragraphWidth = 0;
      if (context) {
        paragraphWidth = this.measureTextWithSpans(context, paragraph, fontSize, fontFamily);
      }
      if (!context || paragraphWidth <= layoutWidth) {
        totalLines += 1;
        continue;
      }
      const words = paragraph.split(/(\s+)/).filter((part) => part.length > 0);
      let currentLineWidth = 0;
      let linesInParagraph = 1;
      for (const word of words) {
        const wordWidth = context ? this.measureTextWithSpans(context, word, fontSize, fontFamily) : 0;
        if (currentLineWidth > 0 && currentLineWidth + wordWidth > layoutWidth) {
          linesInParagraph += 1;
          currentLineWidth = wordWidth;
        } else {
          currentLineWidth += wordWidth;
        }
      }
      totalLines +=
        linesInParagraph === 1 && paragraphWidth > layoutWidth
          ? Math.max(1, Math.ceil(paragraphWidth / layoutWidth))
          : linesInParagraph;
    }
    return Math.max(totalLines, 1) * lineHeight;
  }

  private convertEmojis() {
    if (inlineEditorHandler.formula || this.processingEmojiConversion) return;
    const value = this.get();
    const caret = this.getCaret();
    const textBeforeCursor = value.substring(0, caret);
    const emojiRegex = /:([a-z0-9_-]+):$/;
    const match = textBeforeCursor.match(emojiRegex);
    if (!match) return;
    const emojiName = match[1];
    const emoji = emojiMap[emojiName as keyof typeof emojiMap];
    if (!emoji) return;

    this.processingEmojiConversion = true;
    const startOffset = textBeforeCursor.length - match[0].length;
    const endOffset = textBeforeCursor.length;
    const newValue = value.slice(0, startOffset) + emoji + value.slice(endOffset);
    const newCaret = startOffset + emoji.length;
    this.set(newValue, newCaret);
    inlineEditorEvents.emit('valueChanged', newValue);
    this.setShowingEmojiList(false);
    setTimeout(() => {
      this.processingEmojiConversion = false;
    }, 10);
  }
}

export const inlineEditorInput = new InlineEditorInput();
